import { useForm } from 'antd/es/form/Form';
import { useCallback, useContext, useState } from 'react';

import {
  useLazyGetDetailsOfExpensesReportQuery,
  type ModelExpenseOfDetailsReport,
} from '@app/api/Analytics';
import { DETAILS_OF_EXPENSES } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { DEFAULT_FILTERS_DATA } from '@app/pages/Analytics/data';
import type { FiltersInterface } from '@app/pages/Analytics/DetailsOfExpensesReport/types';
import { useShownReportPeriod } from '@app/pages/Analytics/hooks';
import type { HandleSearchDataProps } from '@app/pages/Analytics/types';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

export const useDetailsOfExpensesReport = () => {
  const [tableData, setTableData] = useState<ModelExpenseOfDetailsReport>();

  const [form] = useForm<FiltersInterface>();

  const { setShownPeriod } = useShownReportPeriod(DETAILS_OF_EXPENSES);
  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const [getDetailsOfExpensesReport, { isLoading }] =
    useLazyGetDetailsOfExpensesReportQuery();

  const handleSearchData = useCallback(
    async ({ filterMonth, filterYear }: HandleSearchDataProps) => {
      try {
        const result = await getDetailsOfExpensesReport({
          ActiveBranchId,
          filterMonth,
          filterYear,
        }).unwrap();

        setTableData(result);
        setShownPeriod({ filterMonth, filterYear });
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [
      ActiveBranchId,
      getDetailsOfExpensesReport,
      setShownPeriod,
      showNotification,
    ]
  );

  const handleReset = useCallback(() => {
    form.resetFields();

    handleSearchData(DEFAULT_FILTERS_DATA);
  }, [form, handleSearchData]);

  const handleSubmitForm = useCallback(
    (values: FiltersInterface) => {
      const { filterMonth, filterYear } = values;
      handleSearchData({
        filterMonth,
        filterYear,
      });
    },
    [handleSearchData]
  );

  return {
    handleReset,
    form,
    handleSubmitForm,
    tableData,
    handleSearchData,
    isLoading,
  };
};
